import { motion } from 'framer-motion';
import { Layers, Building2, UserCircle2 } from 'lucide-react';
import { SceneFrame } from './_atoms';

const pillars = [
  { Icon: Layers,       label: 'Entity Category', tag: 'drives what the system does',      color: 'from-teal-500 to-teal-700',    ring: 'ring-teal-500/30' },
  { Icon: UserCircle2,  label: 'Role',              tag: 'drives what the user can do',      color: 'from-amber-500 to-amber-700',  ring: 'ring-amber-500/30' },
  { Icon: Building2,    label: 'Entity',          tag: 'is where the assignment is scoped', color: 'from-indigo-500 to-indigo-700', ring: 'ring-indigo-500/30' },
];

export default function ArchIntroScene() {
  return (
    <SceneFrame
      eyebrow="User Management Architecture"
      title="Three pillars of access"
      subtitle="Eclaims controls access through three interdependent concepts. Together they guarantee a precise, auditable context for every user — no cross-contamination, no ambient access."
    >
      <div className="grid grid-cols-3 gap-6 mt-6">
        {pillars.map((p, i) => (
          <motion.div
            key={p.label}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.3 + i * 0.22, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${p.color} blur-2xl opacity-30`} />
            <div className={`relative rounded-3xl bg-white border border-[#E5E7EB] p-8 text-center shadow-sm ring-4 ${p.ring}`}>
              <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-4 shadow-md`}>
                <p.Icon size={24} className="text-white" />
              </div>
              <h3 className="font-heading font-extrabold text-[18px] text-[#0A0A0A] mb-1">{p.label}</h3>
              <p className="font-body text-[12.5px] text-[#6B7280] leading-snug">{p.tag}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="mt-10 rounded-2xl border-2 border-teal-600/20 bg-teal-50/60 p-5 flex items-start gap-3 max-w-4xl"
      >
        <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white text-[12px] font-bold">!</span>
        </div>
        <div>
          <p className="font-heading font-bold text-[14px] text-teal-900 mb-1">Key design principle</p>
          <p className="font-body text-[13px] text-teal-900/90 leading-relaxed">
            No two entity categories contaminate each other's logic. Every user operates within a precise,
            auditable context — with module behavior, business logic and the role catalog all determined by the entity category.
          </p>
        </div>
      </motion.div>
    </SceneFrame>
  );
}
